<template>
  <div class="app-file-upload">
    <button type="button" class="app-file-upload__header" v-b-toggle="accordionId">
      <span class="app-file-upload__header-icon"><AppIcon name="attach_file" /></span>
      <span class="app-file-upload__header-title">{{ label }}</span>
      <AppIcon name="chevron-down" class="app-file-upload__chevron" />
    </button>
    <b-collapse :id="accordionId">
      <div class="app-file-upload__body">
        <small v-if="help" class="app-file-upload__help">{{ help }}</small>
        <b-form-file
          v-model="selectedFiles"
          multiple
          :disabled="disabled || files.length >= maxFiles"
          :placeholder="placeholder"
          @input="addFiles"
        />
        <div v-if="files.length" class="app-file-upload__list">
          <div v-for="(file, index) in files" :key="file.path || file.name + index" class="app-file-upload__item">
            <AppIcon name="attach_file" />
            <span>{{ file.name || 'Archivo adjunto' }} <small>{{ fileSize(file.size) }}</small></span>
            <b-button variant="link" size="sm" :disabled="disabled" @click="removeFile(index)">
              <AppIcon name="close" label="Quitar" />
            </b-button>
            <audio v-if="isAudioFile(file)" class="app-file-upload__audio" :src="audioSource(file)" controls preload="metadata"></audio>
          </div>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
export default {
  name: 'AppFileUpload',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Archivos adjuntos',
    },
    help: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Elegí archivos o arrastralos aquí',
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024,
    },
    disabled: Boolean,
  },
  data() {
    return {
      selectedFiles: null,
      audioPreviews: [],
    }
  },
  computed: {
    files() {
      return Array.isArray(this.value) ? this.value : []
    },
    accordionId() {
      return 'app-file-upload-' + this._uid
    },
  },
  methods: {
    addFiles(files) {
      var selected = Array.isArray(files) ? files : (files ? [files] : [])
      var available = this.maxFiles - this.files.length
      var nextFiles = this.files.slice()
      var rejected = []

      selected.slice(0, Math.max(available, 0)).forEach(file => {
        if (Number(file.size || 0) > this.maxFileSize) {
          rejected.push('"' + file.name + '" supera el máximo de ' + this.fileSize(this.maxFileSize))
          return
        }
        nextFiles.push(file)
        this.createAudioPreview(file)
      })

      if (selected.length > available) rejected.push('Solo se pueden adjuntar hasta ' + this.maxFiles + ' archivos')
      if (rejected.length) this.$emit('validation-error', rejected.join('. '))
      this.$emit('input', nextFiles)
      this.selectedFiles = null
    },
    removeFile(index) {
      this.revokeAudioPreview(this.files[index])
      var nextFiles = this.files.slice()
      nextFiles.splice(index, 1)
      this.$emit('input', nextFiles)
    },
    fileSize(bytes) {
      var value = Number(bytes || 0)
      if (!value) return ''
      if (value < 1024 * 1024) return (value / 1024).toFixed(value < 1024 * 100 ? 0 : 1) + ' KB'
      return (value / (1024 * 1024)).toFixed(1) + ' MB'
    },
    isAudioFile(file) {
      if (!file) return false
      if (file.mime && file.mime.indexOf('audio/') === 0) return true
      if (file.type && file.type.indexOf('audio/') === 0) return true
      return /\.(mp3|wav|ogg|oga|m4a|aac|flac|webm)$/i.test(file.name || '')
    },
    createAudioPreview(file) {
      if (!this.isAudioFile(file) || file.path || file.url || typeof URL === 'undefined') return
      if (this.audioPreviews.some(preview => preview.file === file)) return
      this.audioPreviews.push({ file: file, url: URL.createObjectURL(file) })
    },
    audioSource(file) {
      if (file.url || file.path) return file.url || file.path
      var preview = this.audioPreviews.find(item => item.file === file)
      return preview ? preview.url : ''
    },
    revokeAudioPreview(file) {
      var index = this.audioPreviews.findIndex(preview => preview.file === file)
      if (index === -1) return
      if (typeof URL !== 'undefined') URL.revokeObjectURL(this.audioPreviews[index].url)
      this.audioPreviews.splice(index, 1)
    },
  },
  beforeDestroy() {
    if (typeof URL !== 'undefined') this.audioPreviews.forEach(preview => URL.revokeObjectURL(preview.url))
  },
}
</script>

<style scoped>
.app-file-upload {
  margin-top: 14px;
  overflow: hidden;
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  background: var(--app-color-surface);
}
.app-file-upload__header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text);
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}
.app-file-upload__header-icon {
  display: inline-flex;
  color: var(--app-color-primary);
}
.app-file-upload__header-title {
  flex: 1;
}
.app-file-upload__chevron {
  color: var(--app-color-text-muted);
  transition: transform 0.15s ease;
}
.app-file-upload__header.collapsed .app-file-upload__chevron {
  transform: rotate(-90deg);
}
.app-file-upload__body {
  padding: 14px;
  border-top: 1px solid var(--app-color-border);
}
.app-file-upload__help {
  display: block;
  margin-bottom: 6px;
  color: var(--app-color-text-muted);
  font-size: 11px;
}
.app-file-upload__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.app-file-upload__item {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface);
  color: var(--app-color-text);
  font-size: 12px;
  flex-wrap: wrap;
}
.app-file-upload__item span {
  overflow: hidden;
  flex: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.app-file-upload__item small {
  color: var(--app-color-text-muted);
}
.app-file-upload__item button {
  flex: none;
  padding: 0;
  color: var(--app-color-danger);
}
.app-file-upload__audio {
  width: 100%;
  height: 32px;
}
</style>
